import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../services/api';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import {
  Plus, FileText, TrendingUp, Users, Building2,
  AlertTriangle, Clock, CheckCircle2, BarChart3,
  ArrowRight, DollarSign, ChevronRight
} from 'lucide-react';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';
const PRODUCT_LABELS = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro', CUSTOM: 'Custom' };

const fmt = (n) => typeof n === 'number' ? `£${n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '£0';
const fmtMo = (n) => fmt(n) + '/mo';

function KpiCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div className="rounded-xl border bg-white p-4 space-y-2" data-testid={testId}
      style={{ borderLeft: `4px solid ${accent || NAVY}` }}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</p>
        <Icon size={15} style={{ color: accent || NAVY, opacity: 0.7 }} />
      </div>
      <p className="text-2xl font-black tabular-nums" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{value}</p>
      {sub && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{sub}</p>}
    </div>
  );
}

function ExpiringBand({ quotes, isSales }) {
  if (!quotes.length) return null;
  const urgent = quotes.filter(q => q.days_left <= 7).length;
  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="expiring-band"
      style={{ background: urgent > 0 ? '#fef2f2' : '#fffbeb', borderColor: urgent > 0 ? '#fecaca' : '#fde68a' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} style={{ color: urgent > 0 ? '#dc2626' : '#d97706' }} />
          <p className="text-sm font-bold" style={{ color: urgent > 0 ? '#991b1b' : '#92400e' }}>
            {quotes.length} quote{quotes.length !== 1 ? 's' : ''} expiring within 30 days
            {urgent > 0 ? ` · ${urgent} within 7 days` : ''}
          </p>
        </div>
        <Link to="/reports?tab=expiring">
          <Button variant="outline" size="sm" className="text-xs gap-1">
            View All <ArrowRight size={11} />
          </Button>
        </Link>
      </div>
      <div className="flex flex-wrap gap-2">
        {quotes.slice(0, 5).map(q => (
          <Link key={q.id} to={`/quotes/${q.id}`}>
            <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              style={{ background: q.days_left <= 7 ? '#fee2e2' : '#fef3c7', border: `1px solid ${q.days_left <= 7 ? '#fca5a5' : '#fde68a'}`, color: q.days_left <= 7 ? '#991b1b' : '#78350f' }}>
              <span className="font-mono font-bold">{q.quote_number}</span>
              <span style={{ opacity: 0.7 }}>·</span>
              <span>{q.customer_name}</span>
              <span className="font-bold">{q.days_left}d</span>
            </div>
          </Link>
        ))}
        {quotes.length > 5 && (
          <div className="flex items-center text-xs px-2.5 py-1.5 rounded-lg"
            style={{ background: '#f1f5f9', color: 'hsl(var(--muted-fg))' }}>
            +{quotes.length - 5} more
          </div>
        )}
      </div>
    </div>
  );
}

function RecentQuoteRow({ q }) {
  const STATUS_STYLE = {
    DRAFT:    { bg: '#f8fafc', color: '#64748b' },
    SENT:     { bg: '#eff6ff', color: '#1d4ed8' },
    ACCEPTED: { bg: '#f0fdf4', color: '#15803d' },
  };
  const s = STATUS_STYLE[q.status] || STATUS_STYLE.DRAFT;
  return (
    <Link to={`/quotes/${q.id}`} className="flex items-center gap-3 py-2.5 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-bold" style={{ color: NAVY }}>{q.quote_number}</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: s.bg, color: s.color }}>{q.status}</span>
        </div>
        <p className="text-xs mt-0.5 truncate" style={{ color: 'hsl(var(--muted-fg))' }}>{q.customer_name} · {PRODUCT_LABELS[q.product_code] || q.product_code}</p>
      </div>
      <ChevronRight size={13} style={{ color: 'hsl(var(--muted-fg))', flexShrink: 0 }} />
    </Link>
  );
}

function PSMDashboard({ summary, expiring, contracts, recentQuotes }) {
  const sent = summary?.by_status?.SENT || 0;
  const accepted = summary?.by_status?.ACCEPTED || 0;
  const total = summary?.total_quotes || 0;
  const winRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

  return (
    <div className="space-y-5">
      {expiring.length > 0 && <ExpiringBand quotes={expiring} isSales />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp}   label="My Pipeline (GBP)"    value={fmtMo(summary?.pipeline_gbp || 0)} sub={summary?.weighted_pipeline_gbp != null && summary.weighted_pipeline_gbp !== summary.pipeline_gbp ? `Weighted: ${fmtMo(summary.weighted_pipeline_gbp)}` : "Monthly value, all currencies"} accent={NAVY}     testId="kpi-pipeline" />
        <KpiCard icon={Clock}        label="Awaiting Signature"    value={sent}        sub="Sent, not yet signed"       accent="#d97706"   testId="kpi-sent" />
        <KpiCard icon={CheckCircle2} label="Won Quotes"            value={accepted}    sub={`${winRate}% win rate`}     accent="#16a34a"   testId="kpi-accepted" />
        <KpiCard icon={DollarSign}   label="Live Contract MRR"     value={fmtMo(contracts?.mrr_gbp || 0)} sub={`${contracts?.total_live_contracts || 0} live contracts`} accent="#0e7490" testId="kpi-mrr" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Recent Quotes</p>
            <Link to="/quotes"><Button variant="ghost" size="sm" className="text-xs gap-1">All quotes <ArrowRight size={11} /></Button></Link>
          </div>
          <div className="divide-y" style={{ '--tw-divide-opacity': 0.5 }}>
            {recentQuotes.length === 0
              ? <p className="text-sm py-4 text-center" style={{ color: 'hsl(var(--muted-fg))' }}>No quotes yet</p>
              : recentQuotes.slice(0, 6).map(q => <RecentQuoteRow key={q.id} q={q} />)}
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>My Stats</p>
          </div>
          <div className="space-y-2.5">
            {[
              ['Total Quotes', total],
              ['Draft', summary?.by_status?.DRAFT || 0],
              ['Sent', sent],
              ['Accepted', accepted],
            ].map(([label, val]) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span style={{ color: 'hsl(var(--muted-fg))' }}>{label}</span>
                <span className="font-bold tabular-nums" style={{ color: NAVY }}>{val}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm pt-1" style={{ borderTop: '1px solid hsl(var(--border))' }}>
              <span style={{ color: 'hsl(var(--muted-fg))' }}>Conversion Rate</span>
              <span className="font-bold" style={{ color: winRate >= 50 ? '#16a34a' : NAVY }}>{winRate}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManagerDashboard({ summary, expiring, contracts, recentQuotes, byUser }) {
  const sent = summary?.by_status?.SENT || 0;
  const accepted = summary?.by_status?.ACCEPTED || 0;
  const total = summary?.total_quotes || 0;

  return (
    <div className="space-y-5">
      {expiring.length > 0 && <ExpiringBand quotes={expiring} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp}   label="Team Pipeline (GBP)"   value={fmtMo(summary?.pipeline_gbp || 0)} sub={summary?.weighted_pipeline_gbp != null && summary.weighted_pipeline_gbp !== summary.pipeline_gbp ? `Weighted: ${fmtMo(summary.weighted_pipeline_gbp)}` : "All currencies, monthly MRR"} accent={NAVY}     testId="kpi-team-pipeline" />
        <KpiCard icon={CheckCircle2} label="Team Win Rate"          value={total > 0 ? `${Math.round((accepted/total)*100)}%` : '—'} sub={`${accepted} won of ${total} quotes`} accent="#16a34a" testId="kpi-win-rate" />
        <KpiCard icon={FileText}     label="Total Quotes"           value={total}     sub={`${sent} awaiting signature`} accent="#d97706"   testId="kpi-total" />
        <KpiCard icon={DollarSign}   label="Live Contracts MRR"     value={fmtMo(contracts?.mrr_gbp || 0)} sub={`${contracts?.total_live_contracts || 0} live contracts`} accent="#0e7490" testId="kpi-contracts-mrr" />
      </div>

      {/* PSM Leaderboard */}
      {byUser.length > 0 && (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Team Performance</p>
            <Link to="/reports"><Button variant="ghost" size="sm" className="text-xs gap-1">Full Reports <ArrowRight size={11} /></Button></Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: NAVY, color: 'white' }}>
                <th className="text-left px-4 py-2 text-xs font-bold">PSM</th>
                <th className="text-center px-3 py-2 text-xs font-bold">Total</th>
                <th className="text-center px-3 py-2 text-xs font-bold">Won</th>
                <th className="text-center px-3 py-2 text-xs font-bold">Rate</th>
                <th className="text-right px-4 py-2 text-xs font-bold">Pipeline GBP</th>
                <th className="text-right px-4 py-2 text-xs font-bold">Won GBP</th>
              </tr>
            </thead>
            <tbody>
              {byUser.slice(0, 8).map((u, i) => {
                const rate = u.total > 0 ? Math.round((u.accepted / u.total) * 100) : 0;
                return (
                  <tr key={u.user_id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                    <td className="px-4 py-2.5 font-medium text-sm" style={{ color: NAVY }}>{u.user_name}</td>
                    <td className="text-center px-3 py-2.5 text-sm tabular-nums">{u.total}</td>
                    <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: '#16a34a', fontWeight: 600 }}>{u.accepted}</td>
                    <td className="text-center px-3 py-2.5 text-sm tabular-nums" style={{ color: rate >= 50 ? '#16a34a' : '#d97706' }}>{rate}%</td>
                    <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: NAVY }}>{fmt(u.pipeline_gbp)}</td>
                    <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: '#16a34a' }}>{fmt(u.won_gbp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>Recent Quotes</p>
            <Link to="/quotes"><Button variant="ghost" size="sm" className="text-xs gap-1">All <ArrowRight size={11} /></Button></Link>
          </div>
          <div className="divide-y">
            {recentQuotes.slice(0, 6).map(q => <RecentQuoteRow key={q.id} q={q} />)}
          </div>
        </div>

        {contracts?.by_psm?.length > 0 && (
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>MRR by PSM (Live Contracts)</p>
            <div className="space-y-2">
              {contracts.by_psm.slice(0, 6).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#f0fdf4', color: '#15803d' }}>{p.contracts}</span>
                    <span style={{ color: NAVY }}>{p.psm_name}</span>
                  </div>
                  <span className="font-bold tabular-nums" style={{ color: '#0e7490' }}>{fmtMo(p.mrr_gbp)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm font-bold pt-2 mt-2" style={{ borderTop: '1px solid hsl(var(--border))' }}>
              <span style={{ color: NAVY }}>Total MRR</span>
              <span style={{ color: '#0e7490' }}>{fmtMo(contracts.mrr_gbp)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAdmin, isManager, isAccounts } = useAuth();
  const navigate = useNavigate();
  const isSales = user?.role === 'SALES';
  const canSeeTeam = isAdmin || isManager || isAccounts;

  const [summary, setSummary]       = useState(null);
  const [expiring, setExpiring]     = useState([]);
  const [contracts, setContracts]   = useState(null);
  const [recentQuotes, setRecent]   = useState([]);
  const [byUser, setByUser]         = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const calls = [
          apiClient.get('/reports/summary'),
          apiClient.get('/reports/expiring-quotes', { params: { days: 30 } }),
          apiClient.get('/reports/contracts-summary'),
          apiClient.get('/quotes/', { params: { page_size: 10, sort: 'created_at', order: 'desc' } }),
        ];
        if (canSeeTeam) calls.push(apiClient.get('/reports/by-user'));
        const results = await Promise.allSettled(calls);
        if (results[0].status === 'fulfilled') setSummary(results[0].value.data);
        if (results[1].status === 'fulfilled') setExpiring(results[1].value.data || []);
        if (results[2].status === 'fulfilled') setContracts(results[2].value.data);
        if (results[3].status === 'fulfilled') setRecent(results[3].value.data?.items || []);
        if (canSeeTeam && results[4]?.status === 'fulfilled') setByUser(results[4].value.data || []);
      } finally { setLoading(false); }
    };
    load();
  }, [canSeeTeam]);

  const title = isSales ? `Welcome back, ${user?.name?.split(' ')[0] || 'PSM'}` : 'Team Dashboard';

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>{title}</h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        {!isAccounts && (
          <Button onClick={() => navigate('/quotes/new')} className="gap-2"
            style={{ background: ORANGE, color: 'white' }}>
            <Plus size={14} /> New Quote
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : canSeeTeam ? (
        <ManagerDashboard summary={summary} expiring={expiring} contracts={contracts} recentQuotes={recentQuotes} byUser={byUser} />
      ) : (
        <PSMDashboard summary={summary} expiring={expiring} contracts={contracts} recentQuotes={recentQuotes} />
      )}
    </div>
  );
}
